import { useEffect, useCallback, useRef } from 'react';
import { fetchAIConversationMessages } from '../../../utils/api';
import { shouldResetConversation } from '../utils/aiChatUtils';

/**
 * AIチャットメッセージ履歴管理フック
 * opening 取得は useAIOpeningMessage に一本化している
 *
 * @param {string} projectId - プロジェクトID
 * @param {string} conversationId - 会話ID
 * @param {object} user - ユーザーオブジェクト
 * @param {function} setMessages - メッセージ設定関数
 * @param {function} setHydrating - 履歴ハイドレーション状態（準備中表示用）
 * @param {function} setHistoryLoading - 履歴読み込み状態設定関数
 * @param {function} convKey - 会話IDキー生成関数
 * @param {function} msgsKey - メッセージキー生成関数
 * @param {function} deletedMsgsKey - 削除メッセージキー生成関数
 * @param {object} messagesRef - メッセージ参照
 * @param {function} setConversationId - 会話IDクリア用
 * @returns {object} 履歴読み込み関数
 */
export function useAIChatMessages(
    projectId,
    conversationId,
    user,
    setMessages,
    setHydrating,
    setHistoryLoading,
    convKey,
    msgsKey,
    deletedMsgsKey,
    messagesRef,
    setConversationId
) {
    const conversationIdRef = useRef(conversationId);
    conversationIdRef.current = conversationId;
    const loadGenRef = useRef(0);

    const loadHistory = useCallback(async () => {
        const loadGen = ++loadGenRef.current;
        const isStale = () => loadGen !== loadGenRef.current || conversationIdRef.current !== conversationId;

        if (!user?.login_id) {
            setHydrating(false);
            return;
        }

        // ピン等からの reset 待ち中は復元しない（フラグ解除後は opening effect が担当）
        if (shouldResetConversation()) {
            setHistoryLoading(false);
            setHydrating(false);
            return;
        }

        // 会話IDがない場合はローカルストレージから読み込む
        if (!conversationId) {
            try {
                const raw = localStorage.getItem(msgsKey(user.login_id));
                const localMessages = raw ? JSON.parse(raw) : [];
                if (Array.isArray(localMessages) && localMessages.length > 0) {
                    setMessages(localMessages);
                }
            } catch (_) {
                // noop
            }
            setHistoryLoading(false);
            setHydrating(false);
            return;
        }

        if (!projectId) {
            setHistoryLoading(false);
            setHydrating(false);
            return;
        }

        setHistoryLoading(true);
        try {
            const res = await fetchAIConversationMessages(projectId, conversationId, 20);
            if (isStale()) return;
            let loaded = Array.isArray(res?.messages) ? res.messages : [];

            loaded = loaded.filter(msg => msg && msg.content && typeof msg.content === 'string' && msg.content.trim().length > 0);

            try {
                const deletedKey = deletedMsgsKey(user.login_id, conversationId);
                if (deletedKey) {
                    const deletedIdsStr = localStorage.getItem(deletedKey);
                    if (deletedIdsStr) {
                        const deletedIds = JSON.parse(deletedIdsStr);
                        if (Array.isArray(deletedIds) && deletedIds.length > 0) {
                            loaded = loaded.filter(m => !deletedIds.includes(m.id));

                            let currentTurn = 0;
                            const turnMap = new Map();
                            let prevUserMessageId = null;
                            for (const msg of loaded) {
                                if (msg.role === "user") {
                                    currentTurn += 1;
                                    turnMap.set(msg.id, currentTurn);
                                    prevUserMessageId = msg.id;
                                } else if (msg.role === "assistant" && prevUserMessageId && turnMap.has(prevUserMessageId)) {
                                    turnMap.set(msg.id, turnMap.get(prevUserMessageId));
                                }
                            }

                            loaded = loaded.map(msg => ({
                                ...msg,
                                turn: turnMap.get(msg.id) || msg.turn
                            }));
                        }
                    }
                }
            } catch (_) {
                // 削除IDの読み込みに失敗した場合は無視
            }

            if (loaded.length > 0) {
                const existing = messagesRef.current;
                const firstLoaded = loaded[0];
                // opening が無い場合でも履歴はそのままセット。先頭 opening は useAIOpeningMessage 側
                if (existing && existing.length > 0 && existing[0]?.role === 'assistant' && firstLoaded?.role !== 'assistant') {
                    const openingMsg = existing[0];
                    setMessages([openingMsg, ...loaded]);
                } else if (existing && existing.length > 0 && existing[0]?.role === 'assistant') {
                    const openingMsg = existing[0];
                    const sameAsFirst = firstLoaded && firstLoaded.role === 'assistant' && firstLoaded.content === openingMsg.content;
                    setMessages(sameAsFirst ? loaded : [openingMsg, ...loaded]);
                } else {
                    setMessages(loaded);
                }
            } else {
                const existing = messagesRef.current;
                if (existing && existing.length > 0) {
                    setMessages(existing);
                }
                // 空なら opening は useAIOpeningMessage が担当
            }
        } catch (err) {
            if (isStale()) return;
            const status = err?.response?.status;
            const code = err?.response?.data?.code;
            const error = err?.response?.data?.error;

            if (status === 404 || code === 'conversation_not_exists' || error === 'conversation_not_found' || error === 'conversation_model_mismatch') {
                if (conversationId && setConversationId) {
                    setConversationId("");
                }
                try {
                    if (user?.login_id) {
                        localStorage.removeItem(convKey(user.login_id));
                        localStorage.removeItem(msgsKey(user.login_id));
                    }
                } catch (_) {
                    // noop
                }
                setMessages([]);
            } else {
                const existing = messagesRef.current;
                if (existing && existing.length > 0) {
                    setMessages(existing);
                }
            }
        } finally {
            if (!isStale()) {
                setHistoryLoading(false);
                setHydrating(false);
            }
        }
    }, [
        projectId,
        conversationId,
        user?.login_id,
        setMessages,
        setHydrating,
        setHistoryLoading,
        convKey,
        msgsKey,
        deletedMsgsKey,
        messagesRef,
        setConversationId
    ]);

    useEffect(() => {
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId, user?.login_id, projectId]);

    return { loadHistory };
}
